import { speciesLabel } from "./memberStats";
import { normaliseRotation } from "@shared/adjudication";

export type SlideType =
  | "title"
  | "event-overview"
  | "species-winners"
  | "biggest-fish"
  | "day-champions"
  | "happy-snaps-section"
  | "happy-snap-winner"
  | "happy-snaps-gallery"
  | "closing";

export interface TitleSlide {
  type: "title";
  eventTitle: string;
  eventDate: string;
  totalCatches: number;
  uniqueCompetitors: number;
}
export interface EventOverviewSlide {
  type: "event-overview";
  registrationCount: number;
  totalCatches: number;
  speciesCounts: Record<string, number>;
  keptCount: number;
  releasedCount: number;
  highlightPhotos: Array<{
    photoUrl: string;
    competitorName: string;
    species: string;
    lengthCm: number;
    displayRotation: 0 | 90 | 180 | 270;
  }>;
}
export interface SpeciesWinnersSlide {
  type: "species-winners";
  species: string;
  speciesDisplay: string;
  winners: Array<{
    rank: number;
    name: string;
    lengthCm: number;
    photoUrl: string | null;
    displayRotation: 0 | 90 | 180 | 270;
  }>;
}
export interface BiggestFishSlide {
  type: "biggest-fish";
  name: string;
  species: string;
  speciesDisplay: string;
  lengthCm: number;
  photoUrl: string | null;
  displayRotation: 0 | 90 | 180 | 270;
}
export interface HappySnapsSectionSlide {
  type: "happy-snaps-section";
}
export interface HappySnapWinnerSlide {
  type: "happy-snap-winner";
  memberName: string;
  caption: string;
  photoUrl: string | null;
}
export interface HappySnapsGallerySlide {
  type: "happy-snaps-gallery";
  photos: Array<{
    memberName: string;
    caption: string;
    photoUrl: string | null;
  }>;
}
export interface ClosingSlide {
  type: "closing";
  sponsors: Array<{ name: string; logoUrl: string | null }>;
}

export interface DayChampionsSlide {
  type: "day-champions";
  seniorChampionName: string | null;
  seniorChampionPoints: number | null;
  juniorChampionName: string | null;
  juniorChampionPoints: number | null;
}

export type Slide =
  | TitleSlide
  | EventOverviewSlide
  | SpeciesWinnersSlide
  | BiggestFishSlide
  | DayChampionsSlide
  | HappySnapsSectionSlide
  | HappySnapWinnerSlide
  | HappySnapsGallerySlide
  | ClosingSlide;

export function buildSlides(
  event: { title: string; eventDate: string },
  leaderboard: {
    categoryRankings: Record<string, any[]>;
    totalFishCount: number;
    competitorFishCounts: Record<string, number>;
  },
  catchPhotoUrls: Record<string, string | null>,
  competitionPhotos: Array<{
    memberName: string;
    caption: string;
    status: string;
    photoUrl: string | null;
  }>,
  sponsorLogos: Array<{ name: string; logoUrl: string | null }>,
  overview?: {
    registrationCount: number;
    catches: Array<{
      species: string;
      status: string;
      keptOrReleased?: string;
      photoUrl?: string | null;
      competitorName?: string;
      lengthCm?: number;
      displayRotation?: number | null;
    }>;
  },
  championData?: {
    seniorChampionName: string | null;
    seniorChampionPoints: number | null;
    juniorChampionName: string | null;
    juniorChampionPoints: number | null;
  },
  /** #144 / #99: catch id -> the admin's chosen display rotation (0, 90, 180 or 270). Keyed the
   * same as catchPhotoUrls. The stored image is never changed; this is applied on display only. */
  catchRotations: Record<string, number | null | undefined> = {},
): Slide[] {
  const rotationFor = (catchId: string | undefined): 0 | 90 | 180 | 270 =>
    normaliseRotation(catchId ? catchRotations[catchId] : undefined);

  const slides: Slide[] = [];
  slides.push({
    type: "title",
    eventTitle: event.title,
    eventDate: event.eventDate,
    totalCatches: leaderboard.totalFishCount || 0,
    uniqueCompetitors: Object.keys(leaderboard.competitorFishCounts || {})
      .length,
  });

  if (overview) {
    const approved = overview.catches.filter((c) => c.status === "APPROVED");
    const speciesCounts: Record<string, number> = {};
    let keptCount = 0,
      releasedCount = 0;
    for (const c of approved) {
      speciesCounts[c.species] = (speciesCounts[c.species] || 0) + 1;
      if (c.keptOrReleased === "RELEASED") releasedCount++;
      else keptCount++;
    }
    const highlightPhotos = approved
      .filter((c) => c.photoUrl && c.competitorName && c.lengthCm)
      .sort((a, b) => (b.lengthCm || 0) - (a.lengthCm || 0))
      .slice(0, 3)
      .map((c) => ({
        photoUrl: c.photoUrl!,
        competitorName: c.competitorName!,
        species: c.species,
        lengthCm: c.lengthCm!,
        displayRotation: normaliseRotation(c.displayRotation),
      }));
    slides.push({
      type: "event-overview",
      registrationCount: overview.registrationCount,
      totalCatches: approved.length,
      speciesCounts,
      keptCount,
      releasedCount,
      highlightPhotos,
    });
  }

  for (const [species, entries] of Object.entries(
    leaderboard.categoryRankings || {},
  )) {
    if (!entries || entries.length === 0) continue;
    slides.push({
      type: "species-winners",
      species,
      speciesDisplay: speciesLabel(species),
      winners: entries
        .slice(0, 3)
        .map((e: any, i: number) => ({
          rank: i + 1,
          name: e.competitorName,
          lengthCm: e.lengthCm,
          photoUrl: catchPhotoUrls[e.submissionId] || null,
          displayRotation: rotationFor(e.submissionId),
        })),
    });
  }

  // Day's Champions slide — after species-winners, before biggest-fish
  if (championData) {
    slides.push({
      type: "day-champions",
      seniorChampionName: championData.seniorChampionName,
      seniorChampionPoints: championData.seniorChampionPoints,
      juniorChampionName: championData.juniorChampionName,
      juniorChampionPoints: championData.juniorChampionPoints,
    });
  }

  let biggest: any = null;
  for (const entries of Object.values(leaderboard.categoryRankings || {})) {
    for (const e of entries as any[]) {
      if (!biggest || e.lengthCm > biggest.lengthCm) biggest = e;
    }
  }
  if (biggest)
    slides.push({
      type: "biggest-fish",
      name: biggest.competitorName,
      species: biggest.species,
      speciesDisplay: speciesLabel(biggest.species),
      lengthCm: biggest.lengthCm,
      photoUrl: catchPhotoUrls[biggest.submissionId] || null,
      displayRotation: rotationFor(biggest.submissionId),
    });

  const approved = competitionPhotos.filter(
    (p) => p.status === "APPROVED" || p.status === "WINNER",
  );
  const winner = competitionPhotos.find((p) => p.status === "WINNER");
  if (approved.length > 0) {
    slides.push({ type: "happy-snaps-section" });
    if (winner)
      slides.push({
        type: "happy-snap-winner",
        memberName: winner.memberName,
        caption: winner.caption || "",
        photoUrl: winner.photoUrl,
      });
    const gallery = approved.filter((p) => p.status === "APPROVED");
    if (gallery.length > 0)
      slides.push({
        type: "happy-snaps-gallery",
        photos: gallery.slice(0, 12),
      });
  }

  slides.push({ type: "closing", sponsors: sponsorLogos });
  return slides;
}

export function navigateSlide(
  current: number,
  total: number,
  direction: "next" | "prev",
): number {
  if (direction === "next") return current < total - 1 ? current + 1 : current;
  return current > 0 ? current - 1 : current;
}
